import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { TicketScanner } from "../components/TicketScanner";
import { StaffAccessManager } from "../components/StaffAccessManager";

export default function MyEvents() {
  const { t } = useTranslation();
  const u = getUser();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all"); // all | published | draft
  const [listState, setListState] = useState("loading"); // loading | ok | error
  const [scanEvent, setScanEvent] = useState(null);
  const [staffEvent, setStaffEvent] = useState(null);
  const [broadcastEvent, setBroadcastEvent] = useState(null);
  const lastScanBtn = useRef(null); // ritorno del focus al pulsante "Scansiona biglietto" alla chiusura
  const load = () => {
    setListState("loading");
    api.get("/me/events").then(({data}) => { setEvents(data); setListState("ok"); }).catch(() => setListState("error"));
  };
  useEffect(() => { if (u?.role === "organization") load(); }, []);
  const cancelEvent = async (id) => {
    if (!window.confirm(t("events.cancel_confirm"))) return;
    try {
      await api.patch(`/events/${id}`, { status: "cancelled" });
      toast.success(t("events.cancelled"));
      load();
    } catch(err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Errore"); }
  };
  if (u?.role !== "organization") return <Layout><div className="yp-container py-8" data-testid="my-events-forbidden">{t("events.only_org")}</div></Layout>;
  const statusLabel = { published: t("events.status_published"), draft: t("events.status_draft"), cancelled: t("events.cancelled"), moved: t("events.status_moved") };
  const shown = events.filter(e => filter === "all" ? true : filter === "published" ? e.status === "published" : e.status === "draft");
  return (<Layout><div className="yp-container py-8" data-testid="my-events-page">
    <div className="flex justify-between items-start mb-6">
      <h1 className="text-3xl font-bold">{t("events.my_events")}</h1>
      <Link to="/events/create" className="yp-btn yp-btn-primary" data-testid="event-create-btn">+ {t("events.create")}</Link>
    </div>
    <div className="flex gap-2 mb-4" role="tablist" data-testid="my-events-filter">
      {[["all", t("events.filter_all")], ["published", t("events.filter_published")], ["draft", t("events.filter_draft")]].map(([k, lbl]) => (
        <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={`yp-btn !py-1.5 !px-4 text-sm ${filter === k ? "yp-btn-primary" : "yp-btn-outline"}`} data-testid={`my-events-filter-${k}`}>{lbl}</button>))}
    </div>
    <div className="space-y-3">
      {listState === "loading" && <div className="text-slate-400" role="status" data-testid="my-events-loading">{t("common.loading")}</div>}
      {listState === "error" && (
        <div className="yp-card p-4 text-sm text-red-600 flex items-center justify-between gap-3" role="alert" data-testid="my-events-error">
          <span>{t("events.error_generic")}</span>
          <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs shrink-0" onClick={load} data-testid="my-events-retry">{t("common.retry")}</button>
        </div>)}
      {listState === "ok" && events.length === 0 && <div className="text-slate-400" data-testid="my-events-empty">{t("events.empty")}</div>}
      {shown.map(e => (
        <div key={e.id} className="yp-card p-5" data-testid="my-event-card">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/events/${e.id}`} className="font-bold hover:underline">{e.title}</Link>
                <span className={`yp-badge ${e.status === "cancelled" ? "yp-badge-rejected" : "yp-badge-approved"}`} data-testid="event-status">{statusLabel[e.status] || e.status}</span>
                <span className="yp-badge bg-slate-100 text-slate-700" data-testid="event-badge-visibility">{e.visibility === "private" ? t("events.badge_private") : t("events.badge_public")}</span>
                <span className="yp-badge bg-slate-100 text-slate-700" data-testid="event-badge-payment">{e.is_paid ? t("events.badge_paid") : t("events.badge_free")}</span>
                {e.has_password && <span className="yp-badge bg-amber-100 text-amber-800" data-testid="event-badge-password">🔒 {t("events.badge_password")}</span>}
              </div>
              <div className="text-xs text-slate-500 mt-2">📍 {e.address}</div>
              <div className="text-xs text-slate-500 mt-1">
                {(e.slots||[]).map(s => new Date(s.start_at).toLocaleString()).join(" · ")}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {t("events.capacity")}: {(e.slots||[]).reduce((a,s) => a + (s.capacity||0), 0)} · {t("events.registered")}: {e.total_registered} · {t("events.waitlist")}: {e.total_waitlist}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto">
              {e.status === "published" && <a href={`${window.location.origin}/events/${e.id}?preview=1`} target="_blank" rel="noopener noreferrer" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="event-preview-btn" title={t("events.preview_hint")}>{t("events.preview")}</a>}
              <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setBroadcastEvent(e)} data-testid="event-broadcast-btn">{t("events.broadcast")}</button>
              <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={(ev) => { lastScanBtn.current = ev.currentTarget; setScanEvent(e); }} data-testid="scan-ticket-btn">{t("scan.open")}</button>
              <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setStaffEvent(e)} data-testid="staff-access-btn">{t("staff.manage_title")}</button>
              <Link to={`/events/${e.id}/edit`} className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="event-edit-btn">{t("events.edit")}</Link>
              {e.status !== "cancelled" && <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-red-600" onClick={() => cancelEvent(e.id)} data-testid="event-cancel-btn">{t("events.cancel_event")}</button>}
            </div>
          </div>
        </div>))}
    </div>
    {scanEvent && <TicketScanner event={scanEvent} onClose={() => { setScanEvent(null); setTimeout(() => lastScanBtn.current?.focus(), 0); }} />}
    {staffEvent && <StaffAccessManager event={staffEvent} onClose={() => setStaffEvent(null)} />}
    {broadcastEvent && <BroadcastModal event={broadcastEvent} onClose={() => setBroadcastEvent(null)} t={t} />}
  </div></Layout>);
}

function BroadcastModal({ event, onClose, t }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/events/${event.id}/recipients-count`).then(({ data }) => setCount(data.count)).catch(() => setCount(0)); }, [event.id]);
  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error(t("events.bc_empty"));
    setBusy(true);
    try { const { data } = await api.post(`/events/${event.id}/broadcast`, { subject, message }); toast.success(t("events.bc_sent", { n: data.sent })); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail === "empty" ? t("events.bc_empty") : t("events.error_generic")); }
    finally { setBusy(false); }
  };
  return (<div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="broadcast-modal">
    <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center"><h3 className="font-semibold">{t("events.broadcast")}</h3><button onClick={onClose} data-testid="broadcast-close" aria-label="✕">✕</button></div>
      <div className="text-sm text-slate-500" data-testid="broadcast-count">{count === null ? "…" : t("events.bc_to", { n: count })}</div>
      <input className="yp-input w-full" placeholder={t("events.bc_subject")} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject" />
      <textarea className="yp-input w-full min-h-[120px]" placeholder={t("events.bc_message")} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="broadcast-message" />
      <button className="yp-btn yp-btn-primary w-full" onClick={send} disabled={busy || count === 0} data-testid="broadcast-send">{t("events.bc_send")}</button>
      {count === 0 && <div className="text-xs text-slate-400" data-testid="broadcast-none">{t("events.bc_none")}</div>}
    </div>
  </div>);
}
